import { ColDef, ColGroupDef, GridApi, RowNode, ValueGetterParams } from "ag-grid-community";
import { RowDataByTargetId } from "../financial-target.types";
import { FINANCIAL_TARGET_ROW_IDS } from "../constants";
import { IDetailView, ISchemaKey, isDimensionColumn } from "helpers/product-collection";
import { MetricsKind } from "helpers/metrics/types";
import { formulaByKind } from "./formula-mapper";
import { List } from "immutable";
import { FinancialTargetFilter, MappedFinancialTarget } from "components/product-collection/footer/types";
import { BAG_VALUE_PARAMS_SEPARATOR } from "helpers/allocation-dimension";
import { safeParseFloat } from "lib/number";
import { DisplayedGridRows } from "reducers/collection-target/types";
import React from "react";
import { ProductCollectionGridView } from "../../product-collection-grid-view";
import { RowComputeParams } from "helpers/metrics/targets/total/types";
/**
 * Receives a list of rows and filter them by filters passed
 * @param filters List of filters to be applied to the list of rows
 * @param rows List of RowNodes from ag-grid
 * @returns List of RowNodes
 */
export const filterRowsByTargetFilters = (filters: FinancialTargetFilter[] = [], rows: RowNode[]): RowNode[] => {
    const [currentFilter, ...restFilters] = filters;

    if (!currentFilter) {
        return rows;
    }

    const filteredRows = rows.filter(({ data }) => {
        const fields = data?.fields;

        // This means it's a row group, so we return false
        if (!fields) {
            return false;
        }

        const rowDataValue = fields[currentFilter.field_name]?.string_value;
        const match = rowDataValue === currentFilter.value;
        return match;
    });

    return filterRowsByTargetFilters(restFilters, filteredRows);
};

/**
 * Helper to infer type on a column
 * @param column ag-grid column
 * @returns type inference if it's ColGroupDef or not
 */
export const isColGroup = (column: ColDef | ColGroupDef): column is ColGroupDef => {
    if ("groupId" in column && column.groupId !== undefined) {
        return true;
    }

    return false;
};

// --------------------- Row calculation functions ---------------------- //
function getValueFromRow(rowId: string, colId: string, targetGridApi: GridApi): null | undefined | number {
    const rowNode = targetGridApi.getRowNode(rowId);
    if (rowNode) {
        return targetGridApi.getValue(colId, rowNode);
    }

    return undefined;
}

function getDoorsTotalValues(params: RowComputeParams) {
    const { metricKind } = params;

    const formulaFunction = formulaByKind[metricKind];
    return formulaFunction(params);
}

function getTargetValue(params: RowComputeParams) {
    const { getterParams, target } = params;
    const { dimensionIds } = getterParams.colDef.cellRendererParams;

    if (!target.values || !dimensionIds) {
        return null;
    }

    const value = sumTargetValuesByDimenionIds(target, dimensionIds);
    return value > 0 ? value : null;
}

function getVariance(params: RowComputeParams, displayedRows: DisplayedGridRows) {
    const { getterParams, target } = params;
    const targetGridApi = getterParams.api;
    const colId = getterParams.column.getColId();
    const targetId = target.targetId;

    const targetValue = displayedRows[FINANCIAL_TARGET_ROW_IDS.TARGET]
        ? getValueFromRow(`${targetId}|${FINANCIAL_TARGET_ROW_IDS.TARGET}`, colId, targetGridApi)
        : getTargetValue(params);
    if (targetValue === null || targetValue === undefined) {
        return null;
    }
    const doorValue = displayedRows[FINANCIAL_TARGET_ROW_IDS.TOTAL]
        ? getValueFromRow(`${targetId}|${FINANCIAL_TARGET_ROW_IDS.TOTAL}`, colId, targetGridApi)
        : getDoorsTotalValues(params);
    if (doorValue === null || doorValue === undefined) {
        return null;
    }

    const variance = targetValue - doorValue;

    return variance;
}

function getVariancePercentage(params: RowComputeParams, displayedRows: DisplayedGridRows) {
    const { getterParams, target } = params;
    const targetGridApi = getterParams.api;
    const colId = getterParams.column.getColId();
    const targetId = target.targetId;

    const targetValue = displayedRows[FINANCIAL_TARGET_ROW_IDS.TARGET]
        ? getValueFromRow(`${targetId}|${FINANCIAL_TARGET_ROW_IDS.TARGET}`, colId, targetGridApi)
        : getTargetValue(params);
    if (targetValue === null || targetValue === undefined) {
        return null;
    }
    const currentValue = displayedRows[FINANCIAL_TARGET_ROW_IDS.TOTAL]
        ? getValueFromRow(`${targetId}|${FINANCIAL_TARGET_ROW_IDS.TOTAL}`, colId, targetGridApi)
        : getDoorsTotalValues(params);
    if (currentValue === null || currentValue === undefined) {
        return null;
    }

    const variance = ((targetValue - currentValue) / targetValue) * 100;
    return variance;
}

export function formatValueToCurrency(value: number | string) {
    const valueAsNumber = Number(value);
    const formattedValue = valueAsNumber.toLocaleString("en-US", { style: "currency", currency: "USD" });
    return formattedValue;
}

function sumTargetValuesByDimenionIds(target, dimensionIds, depth = 0, accPath = "", accTotal = 0) {
    // When depth is 0, we don't prepend `BAG_VALUE_PARAMS_SEPARATOR`
    const pathPrefix = `${accPath}${depth > 0 ? BAG_VALUE_PARAMS_SEPARATOR : ""}`;

    return dimensionIds[depth].reduce((acc, dimensionId) => {
        const path = `${pathPrefix}${dimensionId}`;
        // Traverse while there is still children
        if (dimensionIds[depth + 1]) {
            acc += sumTargetValuesByDimenionIds(target, dimensionIds, depth + 1, path, accTotal);
        } else {
            const value = safeParseFloat(target.values[path]?.string_value, 0);
            acc += value;
        }
        return acc;
    }, accTotal);
}
// --------------------- End Row calculation functions ---------------------- //

/**
 * Responsible for mapping values to the financial target grid columns, specifically
 * the doors related columns, which are use by metrics
 * @param filteredRowDataByTargetId List of RowNodes that were filtered based on targets
 * @param mainGridApi GridApi of the main grid
 * @param gridViewRef ProductCollectionGridView ref
 * @param getterParams Params passed by ag-grid valueGetter API
 * @param targets Financial Targets object
 * @param displayedRows
 * @param isCollapseModeOn parameter to be used to override the metric to be used for the calculation
 * @returns
 */
export const getDoorTargetValue = (
    filteredRowDataByTargetId: RowDataByTargetId,
    mainGridApi: GridApi,
    gridViewRef: React.RefObject<ProductCollectionGridView>,
    getterParams: ValueGetterParams,
    targets: MappedFinancialTarget[],
    displayedRows: DisplayedGridRows,
    isCollapseModeOn: boolean
) => {
    if (!isDimensionColumn(getterParams.column)) {
        return null;
    }

    const rowId: string | `${string}|${keyof typeof FINANCIAL_TARGET_ROW_IDS}` = getterParams.node.id;
    const shouldDisplay = Object.keys(displayedRows).some((key) => rowId.includes(key) && displayedRows[key]);

    if (!shouldDisplay) {
        return null;
    }

    const calculationMapper = {
        [FINANCIAL_TARGET_ROW_IDS.TOTAL]: getDoorsTotalValues,
        [FINANCIAL_TARGET_ROW_IDS.TARGET]: getTargetValue,
        [FINANCIAL_TARGET_ROW_IDS.VARIANCE]: getVariance,
        [FINANCIAL_TARGET_ROW_IDS.VARIANCE_PERCENTAGE]: getVariancePercentage,
    };

    const metricsKind: MetricsKind = getterParams.node.data.targetMetricsType;

    // since when building the rows we pass the id as "targetId|type of row"
    // we can split these values to know which calculation is necessary on a
    // given row and what is the current target id, necesary to find the
    // delivery#doors coordinates and get the financial target values
    const [targetId, targetFunction] = rowId.split("|");

    // checking if it's a metrics column, otherwise return an empty value.
    // This is assuming this is metric columns
    if (getterParams.colDef.cellRendererParams?.metric?.kind !== metricsKind && !isCollapseModeOn) {
        return null;
    }

    const target = targets.find((t) => t.targetId === targetId);
    if (!target) {
        return undefined;
    }

    const rowCalculationParams: RowComputeParams = {
        gridViewRef,
        mainGridApi,
        mainRowNodes: filteredRowDataByTargetId[targetId],
        getterParams,
        target,
        metricKind: metricsKind,
    };

    return calculationMapper[targetFunction](rowCalculationParams, displayedRows);
};

/**
 * Find a schema field object based on the detail view key passed
 * @param detailViewKey Object key from detail view to match the schema index
 * @param schema Merchant schema
 * @returns The schema field thas has that detail view key
 */
export const getSchemaFieldByDetailView = (detailViewKey: keyof IDetailView, schema: List<ISchemaKey>) => {
    const item = schema.find((v: ISchemaKey) => v.detail_view?.[detailViewKey]);
    return item;
};
